/**
 * ProcessingHistoryTimeline - Shows past bank submission attempts
 *
 * Renders an expandable list of processing application entries with
 * status color coding, processor name, bank, and rejection reasons.
 *
 * Responsive: stacks vertically on mobile.
 */

import React, { useState } from 'react';
import { Tag } from 'primereact/tag';
import { Skeleton } from 'primereact/skeleton';
import { Button } from 'primereact/button';
import { getReasonLabel } from '../../services/leadProcessingService';

const STATUS_COLORS = {
    rejected: { border: 'var(--red-400)', bg: 'var(--red-50)', text: 'text-red-600' },
    approved: { border: 'var(--green-400)', bg: 'var(--green-50)', text: 'text-green-600' },
    submitted: { border: 'var(--blue-400)', bg: 'var(--blue-50)', text: 'text-blue-600' },
    in_progress: { border: 'var(--surface-400)', bg: 'var(--surface-50)', text: 'text-600' },
    withdrawn: { border: 'var(--surface-400)', bg: 'var(--surface-50)', text: 'text-500' },
    customer_declined: { border: 'var(--orange-400)', bg: 'var(--orange-50)', text: 'text-orange-600' },
};

const STATUS_LABELS = {
    in_progress: 'In Progress',
    submitted: 'Submitted',
    approved: 'Approved',
    rejected: 'Rejected',
    customer_declined: 'Customer Declined',
    withdrawn: 'Withdrawn',
};

const ProcessingHistoryTimeline = ({ applications = [], loading = false }) => {
    const [expanded, setExpanded] = useState(false);

    if (loading) {
        return (
            <div className="p-3">
                {[1, 2].map(i => (
                    <div key={i} className="mb-2">
                        <Skeleton width="100%" height="3rem" />
                    </div>
                ))}
            </div>
        );
    }

    if (!applications || applications.length === 0) {
        return (
            <div className="text-500 text-sm text-center p-3">
                <i className="pi pi-history mr-2" />
                No processing attempts yet
            </div>
        );
    }

    const visibleApps = expanded ? applications : applications.slice(0, 2);

    return (
        <div>
            <div className="flex align-items-center gap-2 mb-2 px-1">
                <i className="pi pi-history text-500 text-sm" />
                <span className="font-semibold text-700 text-sm">Processing History ({applications.length} attempt{applications.length !== 1 ? 's' : ''})</span>
            </div>

            {visibleApps.map((app, idx) => {
                const colors = STATUS_COLORS[app.status] || STATUS_COLORS.in_progress;
                return (
                    <div
                        key={app.id || idx}
                        className="mb-2 border-round p-2 md:p-3"
                        style={{ borderLeft: `3px solid ${colors.border}`, backgroundColor: colors.bg }}
                    >
                        {/* Header: processor + bank */}
                        <div className="flex flex-column md:flex-row md:align-items-center gap-1 md:gap-2 mb-1">
                            <span className="font-semibold text-900 text-sm">{app.processor?.name || 'Unknown'}</span>
                            {app.bank_name && (
                                <span className="flex align-items-center gap-1 text-600 text-xs">
                                    <i className="pi pi-building text-xs" />
                                    {app.bank_name}
                                </span>
                            )}
                        </div>

                        {/* Status */}
                        <div className="flex flex-wrap align-items-center gap-2 mb-1">
                            <Tag
                                value={STATUS_LABELS[app.status] || app.status}
                                severity={app.status === 'approved' ? 'success' : app.status === 'rejected' ? 'danger' : app.status === 'customer_declined' ? 'warning' : 'info'}
                                className="text-xs"
                            />
                            {app.loan_amount && (
                                <span className="text-xs text-500">
                                    {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(app.loan_amount)}
                                </span>
                            )}
                            {app.interest_rate && (
                                <span className="text-xs text-500">{app.interest_rate}%</span>
                            )}
                        </div>

                        {/* Rejection details */}
                        {app.status === 'rejected' && (
                            <div className="mt-1">
                                {app.rejection_reason_code && (
                                    <div className="text-red-700 text-xs font-semibold">
                                        {app.rejection_reason_label || getReasonLabel(app.rejection_reason_code)}
                                    </div>
                                )}
                                {app.rejection_detail && (
                                    <div className="text-red-600 text-xs font-italic mt-1">"{app.rejection_detail}"</div>
                                )}
                            </div>
                        )}

                        {/* Dates */}
                        <div className="flex flex-wrap gap-2 mt-1 text-400 text-xs">
                            {app.submitted_at && <span>Submitted: {app.submitted_at}</span>}
                            {app.bank_responded_at && <span>Responded: {app.bank_responded_at}</span>}
                            {!app.submitted_at && app.created_at && <span>Created: {app.created_at}</span>}
                        </div>
                    </div>
                );
            })}

            {applications.length > 2 && (
                <Button
                    label={expanded ? 'Show Less' : `Show All (${applications.length})`}
                    icon={expanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                    className="p-button-text p-button-sm w-full"
                    onClick={() => setExpanded(!expanded)}
                />
            )}
        </div>
    );
};

export default ProcessingHistoryTimeline;
